import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BenefitCategory, TenantGroupBenefitApiService } from '@flexyoo/api';
import {
  BenefitProposedByType,
  BenefitSupplierType,
  CarFuelType,
  FinancingPlanType,
  PaymentPeriodType as PaymentPeriodTypeEnum,
  UnitType,
} from '@flexyoo/api/models';
import { BenefitParameters } from '@flexyoo/benefits/declarations';
import { LoggerService } from '@flexyoo/core';
import { Parameter } from '@flexyoo/models';
import { Pager, PaginatedItemsResponse } from '@flexyoo/shared/models';
import { PopUpService } from '@flexyoo/shared/ui';
import {
  ITenantBenefitOverview,
  TenantBenefitChildListItem,
} from '@flexyoo/tenant-benefits/declarations';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  IOptionForInsurance,
  IOptionForInternet,
  IOptionForPension,
  TenantBenefitChildDetail,
} from '../../../shared/models/tenant-benefits/tenant-benefit-child-detail';
import { TenantBenefitDetail3Lang } from '../../../shared/models/tenant-benefits/tenant-benefit-detail';
import { UpdatePolicyFileDto } from '../../../shared/models/tenant-benefits/updatePolicyDto';
import { TenantBenefitMobilityCar } from '../declarations/models/tenant-benefit-mobility-car.model';

@Injectable({ providedIn: 'root' })
export class TenantBenefitsService {
  private readonly logger = inject(LoggerService);
  private readonly tenantGroupBenefitApiService = inject(TenantGroupBenefitApiService);

  constructor(
    private http: HttpClient,
    private readonly popUpService: PopUpService
  ) {}

  // TenantBenfit - List
  getTenantBenefitsTree(
    category?: BenefitCategory,
    isMobilityBudget?: boolean
  ): Observable<ITenantBenefitOverview[]> {
    return this.tenantGroupBenefitApiService.getOverview({
      Category: category,
      IsMobilityBudgetFinancingPlanType: isMobilityBudget,
    });
  }

  // TenantBenfit - List
  getTenantBenefitsTable(
    pager: Pager,
    showInactiveBenefits = false,
    familyType = '',
    category = '',
    benefitName = '',
    financingPlanType: FinancingPlanType | '' = ''
  ): Observable<PaginatedItemsResponse<TenantBenefitChildListItem>> {
    const financingPlanTypeFilterValue = financingPlanType
      ? `&FinancingPlanType=${financingPlanType}`
      : '';
    return this.http.get<PaginatedItemsResponse<TenantBenefitChildListItem>>(
      `${environment.api}/tenant-benefit-children/filter?PageNumber=${pager.pageIndex}&PageSize=${pager.pageSize}&ShowInactiveBenefits=${showInactiveBenefits}&FamilyType=${familyType}&TenantGroupBenefitId=${category}&Name=${benefitName}${financingPlanTypeFilterValue}`
    );
  }

  // TenantBenfit - List
  getTenantBenefitChildren(id: number): Observable<TenantBenefitChildDetail> {
    return this.http.get<TenantBenefitChildDetail>(
      `${environment.api}/tenant-benefit-children/${id}`
    );
  }

  getUsedSupplierTypesInCategory(
    tenantGroupBenefitId: number,
    excludeChildId?: number
  ): Observable<BenefitSupplierType[]> {
    if (!tenantGroupBenefitId) {
      return of([]);
    }

    return this.getTenantBenefitsTree().pipe(
      map((overview) => {
        const children =
          overview.find((item) => item.Id === tenantGroupBenefitId)?.TenantBenefitChildren ?? [];

        return [
          ...new Set(
            children
              .filter(
                (child) =>
                  child.ProposedByType === BenefitProposedByType.Employee &&
                  child.Id !== excludeChildId &&
                  child.SupplierType !== BenefitSupplierType.None
              )
              .map((child) => child.SupplierType)
          ),
        ];
      })
    );
  }

  toggleTenantBenefitStatus(benefit: TenantBenefitChildListItem): Observable<boolean> {
    return this.http
      .put(
        `${environment.api}/tenant-benefit-children/${benefit.Id}/toggle-is-active`,
        !benefit.IsActive
      )
      .pipe(
        map((d: boolean) => {
          return d;
        })
      );
  }

  // TenantBenefit
  getTenantBenefit(id: number): Observable<TenantBenefitDetail3Lang> {
    return this.http.get<TenantBenefitDetail3Lang>(`${environment.api}/tenant-group/benefit/${id}`);
  }

  saveTenantBenefit3Lang(detail: TenantBenefitDetail3Lang): Observable<TenantBenefitDetail3Lang> {
    return this.http.put<TenantBenefitDetail3Lang>(
      `${environment.api}/tenant-group/benefit/${detail.Id}`,
      detail
    );
  }

  downloadTenantBenefitPolicy3Lang(tenantBenefitId: number, lang): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${
        environment.api
      }/tenant-group/benefit/${tenantBenefitId}/policy/${lang.toLowerCase()}/download`,
      { responseType: 'blob', observe: 'response' }
    );
  }

  uploadTenantBenefitPolicy3Lang(
    tenantBenefitId: number,
    formData: FormData,
    lang
  ): Observable<UpdatePolicyFileDto> {
    return this.http
      .post(
        `${
          environment.api
        }/tenant-group/benefit/${tenantBenefitId}/policy/${lang.toLowerCase()}/upload`,
        formData,
        { reportProgress: true, observe: 'events' }
      )
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            return new UpdatePolicyFileDto(Math.round((100 * event.loaded) / event.total));
          } else if (event.type === HttpEventType.Response) {
            const update = event.body as UpdatePolicyFileDto;
            update.ProgressPercentage = 100;
            return update;
          }
        })
      );
  }
  saveTenantBenefitChild(
    Id: number,
    TenantBenefitId: number,
    NameNl: string,
    NameFr: string,
    NameEn: string,
    ImageFile: File,
    ImageFileName: string,
    Collar: string,
    InfoNl: string,
    InfoFr: string,
    InfoEn: string,
    IsAvailableByTag: boolean,
    SupplierType: number,
    ProposedByType: BenefitProposedByType,
    TenantGroupTagIds: number[],
    Parameters: BenefitParameters[],
    IsActive: boolean,
    InsuranceOptions: IOptionForInsurance[],
    HomeInternetOptions: IOptionForInternet[],
    PensionOptions: IOptionForPension[],
    MonthlyContribution?: number,
    LegalStartDate?: string,
    LegalEndDate?: string,
    ExcludedFundingTypeIds?: number[],
    ExcludedTenantGroupFundingTypeIds?: string[],
    LeasingPeriod = 12,
    PaymentPeriodType: PaymentPeriodTypeEnum = PaymentPeriodTypeEnum.Default,
    IsPrimesAndBonusesFinancingPlanType?: boolean,
    IsSalarySacrificeFinancingPlanType?: boolean,
    IsMobilityBudgetFinancingPlanType?: boolean,
    FuelType?: CarFuelType,
    CO2EmissionRate?: number,
    CatalogValue?: number,
    MobilityBudgetCar?: TenantBenefitMobilityCar,
    IsCalculatedInFlexyooMobilityBudget?: boolean,
    EmployeePopulationIds?: number[],
    ResidualValue?: number,
    ResidualValueType?: UnitType,
    ShowResidualValue?: boolean
  ): Observable<number> {
    const formData = new FormData();
    formData.append('Name', NameEn);
    formData.append('NameNl', NameNl);
    formData.append('NameFr', NameFr);
    formData.append('NameEn', NameEn);

    if (ImageFile) {
      formData.append('ImageFile', ImageFile);
    } else if (ImageFileName) {
      formData.append('ImageFileName', ImageFileName);
    }
    formData.append('Collar', Collar);
    formData.append('InfoNl', InfoNl);
    formData.append('InfoFr', InfoFr);
    formData.append('InfoEn', InfoEn);
    formData.append('IsAvailableByTag', JSON.stringify(IsAvailableByTag));

    if (MonthlyContribution !== null && MonthlyContribution !== undefined) {
      formData.append('MonthlyContribution', MonthlyContribution.toString());
    }

    formData.append('LeasingPeriod', LeasingPeriod.toString());
    formData.append('PaymentPeriodType', PaymentPeriodType.toString());
    formData.append('SupplierType', JSON.stringify(SupplierType));
    formData.append('ProposedByType', JSON.stringify(ProposedByType));

    if (LegalStartDate) {
      formData.append('LegalStartDate', LegalStartDate);
    }
    if (LegalEndDate) {
      formData.append('LegalEndDate', LegalEndDate);
    }

    for (let i = 0; i < TenantGroupTagIds.length; i++) {
      formData.append('TenantGroupTagIds', TenantGroupTagIds[i].toString());
    }
    const filteredParams = Parameters.filter((p) => p.ParameterId !== Parameter.MonthsInUse);
    for (let i = 0; i < filteredParams.length; i++) {
      formData.append(`Parameters[${i}][Id]`, filteredParams[i].Id?.toString());
      formData.append(`Parameters[${i}][ParameterId]`, filteredParams[i].ParameterId?.toString());
      formData.append(`Parameters[${i}][Type]`, filteredParams[i].Type);
      formData.append(`Parameters[${i}][Value]`, filteredParams[i].Value);
      formData.append(`Parameters[${i}][InfoNL]`, filteredParams[i].InfoNL);
      formData.append(`Parameters[${i}][InfoFR]`, filteredParams[i].InfoFR);
    }

    for (let i = 0; i < InsuranceOptions?.length; i++) {
      formData.append(`InsuranceOptions[${i}][OptionNameNl]`, InsuranceOptions[i].OptionNameNl);
      formData.append(`InsuranceOptions[${i}][OptionNameFr]`, InsuranceOptions[i].OptionNameFr);
      formData.append(`InsuranceOptions[${i}][OptionNameEn]`, InsuranceOptions[i].OptionNameEn);
      formData.append(
        `InsuranceOptions[${i}][AdultPrice]`,
        InsuranceOptions[i].AdultPrice.toString()
      );
      formData.append(
        `InsuranceOptions[${i}][ChildPrice]`,
        InsuranceOptions[i].ChildPrice.toString()
      );
    }

    for (let i = 0; i < HomeInternetOptions?.length; i++) {
      formData.append(
        `HomeInternetOptions[${i}][OptionNameNl]`,
        HomeInternetOptions[i].OptionNameNl
      );
      formData.append(
        `HomeInternetOptions[${i}][OptionNameFr]`,
        HomeInternetOptions[i].OptionNameFr
      );
      formData.append(
        `HomeInternetOptions[${i}][OptionNameEn]`,
        HomeInternetOptions[i].OptionNameEn
      );
      formData.append(
        `HomeInternetOptions[${i}][MonthlyPrice]`,
        HomeInternetOptions[i].MonthlyPrice.toString()
      );
    }

    for (let i = 0; i < PensionOptions?.length; i++) {
      formData.append(`PensionOptions[${i}][OptionNameNl]`, PensionOptions[i].OptionNameNl);
      formData.append(`PensionOptions[${i}][OptionNameFr]`, PensionOptions[i].OptionNameFr);
      formData.append(`PensionOptions[${i}][OptionNameEn]`, PensionOptions[i].OptionNameEn);
      formData.append(`PensionOptions[${i}][Amount]`, PensionOptions[i].Amount.toString());
    }

    if (ExcludedFundingTypeIds?.length) {
      for (let i = 0; i < ExcludedFundingTypeIds.length; i++) {
        formData.append('ExcludedFundingTypeIds', ExcludedFundingTypeIds[i].toString());
      }
    }
    if (ExcludedTenantGroupFundingTypeIds?.length) {
      for (let i = 0; i < ExcludedTenantGroupFundingTypeIds.length; i++) {
        formData.append(
          'ExcludedTenantGroupFundingTypeIds',
          ExcludedTenantGroupFundingTypeIds[i].toString()
        );
      }
    }

    formData.append('IsActive', JSON.stringify(IsActive));

    formData.append(
      'IsPrimesAndBonusesFinancingPlanType',
      JSON.stringify(IsPrimesAndBonusesFinancingPlanType)
    );
    formData.append(
      'IsSalarySacrificeFinancingPlanType',
      JSON.stringify(IsSalarySacrificeFinancingPlanType)
    );

    if (IsMobilityBudgetFinancingPlanType) {
      formData.append('IsMobilityBudgetFinancingPlanType', 'true');
      formData.append('UseTCOCalculationLogic', `${IsCalculatedInFlexyooMobilityBudget}`);
      if (EmployeePopulationIds?.length) {
        for (let i = 0; i < EmployeePopulationIds.length; i++) {
          formData.append('EmployeePopulationIds', EmployeePopulationIds[i].toString());
        }
      }
    }

    if (MobilityBudgetCar) {
      Object.entries(MobilityBudgetCar).forEach(([key, value]) => {
        formData.append('Car.TCODetail.' + key, (value ?? '').toString());
      });
    }

    if (FuelType) {
      formData.append('Car.FuelType', FuelType.toString());
    }
    if (CO2EmissionRate) {
      formData.append('Car.CO2EmissionRate', CO2EmissionRate.toString());
    }
    if (CatalogValue) {
      formData.append('Car.CatalogValue', CatalogValue.toString());
    }

    if (ResidualValue) {
      formData.append('ResidualValue', ResidualValue.toString());
    }
    if (ResidualValueType !== null && ResidualValueType !== undefined) {
      formData.append('ResidualValueType', ResidualValueType.toString());
    }

    if (ShowResidualValue !== null && ShowResidualValue !== undefined) {
      formData.append('ShowResidualValue', JSON.stringify(ShowResidualValue));
    }

    if (Id === 0 || Id === null || Id === undefined) {
      formData.append('TenantBenefitId', JSON.stringify(TenantBenefitId));
      return this.http.post<number>(`${environment.api}/tenant-benefit-children`, formData);
    } else if (Id !== 0 && Id !== null && Id !== undefined) {
      return this.checkUpdate(TenantBenefitId, 'update').pipe(
        switchMap(() =>
          this.http.put<number>(`${environment.api}/tenant-benefit-children/${Id}`, formData)
        )
      );
    } else {
      this.logger.warn('Mistake happened when saving tenant-benefit-child');
    }
  }

  deleteTenantBenefitChild(id: number) {
    const url = `${environment.api}/tenant-benefit-children/${id}`;

    return this.checkUpdate(id, 'delete').pipe(switchMap(() => this.http.delete(url)));
  }

  checkTenantBenefitChildUpdate(id: number) {
    const url = `${environment.api}/tenant-benefit-children/${id}/employee-benefits/selected-state-count`;
    return this.http.get(url).pipe(
      map((count) => {
        return {
          BenefitInBasketCount: count,
          CanEdit: !!count,
        };
      })
    );
  }

  private checkUpdate(id: number, mode: 'update' | 'delete') {
    return this.checkBenefitsCanUpdate(id).pipe(
      switchMap((canEdit) =>
        canEdit
          ? mode === 'update'
            ? of(true)
            : this.confirmDeleteDialog()
          : mode === 'update'
            ? this.confirmUpdateProtectedDialog()
            : this.confirmDeleteProtectedDialog()
      ),
      switchMap((result) => {
        this.logger.log('hr benefit check update', result);
        return result ? of(result) : throwError('interrupt');
      })
    );
  }

  private checkBenefitsCanUpdate(id: number) {
    return this.checkTenantBenefitChildUpdate(id).pipe(
      map((result) => result.BenefitInBasketCount === 0)
    );
  }

  private confirmUpdateProtectedDialog(): Observable<boolean> {
    return this.popUpService.openConfirmDialog({
      title: 'hr.update-benefit-warning.title',
      text: 'hr.update-benefit-warning.text',
      textYes: 'hr.update-benefit-warning.ok',
      textNo: 'hr.update-benefit-warning.cancel',
    });
  }

  private confirmDeleteProtectedDialog(): Observable<boolean> {
    return this.popUpService.openConfirmDialog({
      title: 'hr.delete-benefit-warning.title',
      text: 'hr.delete-benefit-warning.text',
      textYes: 'general.yes-delete',
      textNo: 'general.cancel',
    });
  }

  private confirmDeleteDialog(): Observable<boolean> {
    return this.popUpService.openConfirmDialog({
      text: 'hr.are-you-sure-remove-benefit',
    });
  }
}
